import React, { useState } from "react";
import Image from "../components/image.js";
import styled from "styled-components";
import Container from "../components/container";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faMinus,
  faPlus,
  faCartShopping,
  faSpinner,
  faShare,
} from "@fortawesome/free-solid-svg-icons";
import { faTrashCan } from "@fortawesome/free-regular-svg-icons";
import {
  GlobalDispatchContext,
  GlobalStateContext,
} from "../context/global-context-provider";
import { Link, useStaticQuery, graphql } from "gatsby";
import Button from "./button.js";
import Paragraph from "./paragraph";
import { _ } from "../utils/i18n";
import MiniShare from "./minishare.js";
import DinteroPayments from "./dintero-payments.js";
import PropTypes from "prop-types";

const Wrapper = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
`;

const ProductWrapper = styled.div`
  width: 100%;
  display: flex;
  align-items: stretch;
  justify-content: space-between;
  padding: 1rem 2rem;
  gap: clamp(1rem, 2vw, 1.5rem);
  background-color: var(--color-white);

  &:nth-child(even) {
    background-color: var(--color-body);
  }
  @media (max-width: ${({ theme }) => theme.breakpoints.phone}) {
    align-items: center;
    padding: 1rem;
    gap: clamp(0.5rem, 2vw, 1.5rem);
  }
`;
const StyledImage = styled(Image)`
  max-width: 110px;
  border: 1px solid var(--color-grey);
  border-radius: var(--border-radius);
`;
const MiddleWrapper = styled.div`
  display: flex;
  flex-flow: column;
  height: 100%;
  justify-content: space-between;
  flex-basis: 75%;
  align-items: flex-start;
  min-height: 110px;
`;

const NameWrapper = styled.div`
  width: 100%;
`;
const StyledLink = styled(Link)`
  width: 100%;
  font-weight: 600;
  font-size: clamp(0.8rem, 2vw, 1.1rem);
  display: block;
  text-decoration: none;
  &:hover {
    text-decoration: underline;
  }
`;
const QuantityBox = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-block: 0.6rem;
  padding-inline: clamp(0.225rem, 2vw, 0.6rem);
  border: 1px solid var(--color-grey);
  border-radius: var(--border-radius);
  background-color: var(--color-white);
  gap: clamp(0.225rem, 2vw, 0.5rem);
`;

const ChangeQtyBtn = styled(FontAwesomeIcon)`
  cursor: pointer;
  color: ${({ $productQty }) => ($productQty === 1 ? "grey" : "black")};
`;
const RightWrapper = styled.div`
  display: flex;
  flex-flow: column;
  justify-content: space-between;
`;
const Price = styled.div`
  display: grid;
  padding-block-end: 0.525rem;
  & p {
    margin: 0;
    text-align: right;
  }
`;

const RemoveButton = styled.button`
  background-color: transparent;
  font-size: clamp(0.9rem, 2vw, 1rem);
  display: flex;
  white-space: nowrap;
  align-items: center;
  color: var(--color-black);
`;
const RemoveIcon = styled(FontAwesomeIcon)`
  margin-left: 10px;
  font-size: 1rem;
`;

const BottomDiv = styled.div`
  display: flex;
  flex-flow: wrap row;
  justify-content: space-between;
  width: 90%;
  margin: 3rem auto;
  padding-block: 1rem;
  gap: 1rem;
  background-color: var(--color-white);
  border-top: 1px solid var(--color-accent3);
  a {
    width: 49%;
    @media (max-width: ${({ theme }) => theme.breakpoints.phone}) {
      width: 100%;
    }
  }
`;
const CartEmptyWrapper = styled.div`
  display: grid;
  place-items: center;
  gap: 1rem;
`;
const CartEmptyIcon = styled(FontAwesomeIcon)`
  color: var(--color-primary);
  background-color: var(--color-grey);
  border-radius: 3rem;
  font-size: 2.6rem;
  padding: 1.1rem;
`;
const LoadingIcon = styled(FontAwesomeIcon)`
  font-size: 1.3rem;
  color: var(--color-white);
  opacity: 0.5;
`;

const Attributes = styled.div`
  font-size: 0.8rem;
  margin-top: 5px;
  margin-bottom: 5px;
`;
const ButtonTitle = styled.div`
  display: flex;
  width: 100%;
  justify-content: space-between;
  padding: 0 2rem;
`;

const EmptyCartMessage = styled.p`
  font-family:var(--font-headings); "sans-serif";
  font-size: 1.5rem;
  color: var(--color-body-text);
  text-align: center;
`;

const ShareButton = styled.button`
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: transparent;
  width: 100%;
  font-size: 1rem;
  color: var(--color-black);
  &:hover {
    text-decoration: underline;
  }
`;

const ShareIcon = styled(FontAwesomeIcon)`
  font-size: 1.3rem;
  margin-left: 15px;
  color: rgba(54, 54, 54, 0.6);
`;

const MiniCart = ({ onClose }) => {
  const { wp, wpProductCategory } = useStaticQuery(graphql`
    query {
      wp {
        wcSettings {
          currency
          checkoutUrl
          cartUrl
        }
      }
      wpProductCategory(crbMasterCategory: { eq: true }) {
        uri
      }
    }
  `);
  const { cart } = React.useContext(GlobalStateContext);
  const dispatch = React.useContext(GlobalDispatchContext);

  const [isLoading, setIsLoading] = useState(false);
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [cartLink, setCartLink] = useState("");

  function removeProduct(id) {
    dispatch({
      type: "UPDATE_CART",
      value: cart.filter((product) => product.id !== id),
    });
  }

  function increaseQty(product) {
    dispatch({ type: "INCREASE_QUANTITY_IN_CART", value: product });
  }

  function decreaseQty(product) {
    dispatch({ type: "REMOVE_FROM_CART", value: product });
  }

  const generateCartLink = () => {
    const cartData = JSON.stringify(
      cart.map((item) => ({
        id: item.id,
        quantity: item.quantity,
      }))
    );
    const newCartLink =
      `${process.env.GATSBY_SITE_URL}` +
      `/shared-cart?data=${encodeURIComponent(cartData)}`;
    setCartLink(newCartLink);
    return newCartLink;
  };

  const handleButtonClick = () => {
    setIsPopupOpen(!isPopupOpen);
    generateCartLink();
  };

  const formatPrice = (price) => {
    const formattedNumber = (number) =>
      number.toLocaleString("no-NO", {
        useGrouping: true,
        groupingSeparator: " ",
      });

    price = parseFloat(price.replace(/[^\d.-]/g, ""));

    return "kr " + formattedNumber(price);
  };

  let total = 0;

  cart.forEach((item) => {
    total += item.priceRaw * item.quantity;
  });
  return (
    <Container maxWidth="md" disableGutters disableMargin disableAnimation>
      {cart && cart.length > 0 ? (
        <Wrapper>
          {cart.map((product) => (
            <ProductWrapper key={product.id}>
              <StyledImage image={product.image} />
              <MiddleWrapper>
                <NameWrapper>
                  <StyledLink to={product.uri}>{product.name}</StyledLink>
                  {product?.sku && (
                    <Paragraph content={product?.sku} fontSize="0.8rem" />
                  )}
                  {product?.attributeLabels && (
                    <>
                      {product?.attributeLabels?.length > 0 ? (
                        product.attributeLabels?.map((attributes, index) => (
                          <Attributes key={index}>
                            {attributes.label} : {attributes.value}
                          </Attributes>
                        ))
                      ) : (
                        <Attributes>
                          {product?.attributeLabels?.label} :{" "}
                          {product?.attributeLabels?.value}
                        </Attributes>
                      )}
                    </>
                  )}
                </NameWrapper>
                <QuantityBox>
                  <ChangeQtyBtn
                    icon={faMinus}
                    size="xs"
                    onClick={() => {
                      setIsLoading(true);
                      decreaseQty(product);
                      setTimeout(() => {
                        setIsLoading(false);
                      }, 2000);
                    }}
                    $productQty={product.quantity}
                  />
                  <Paragraph
                    fontSize="clamp(0.8rem, 2vw, 1rem)"
                    content={product.quantity}
                    align="center"
                    innerStyle={{
                      width: "clamp(2rem, 2vw, 3.525rem)",
                      margin: 0,
                    }}
                  />
                  <ChangeQtyBtn
                    icon={faPlus}
                    size="xs"
                    onClick={() => {
                      setIsLoading(true);
                      increaseQty(product);
                      setTimeout(() => {
                        setIsLoading(false);
                      }, 2000);
                    }}
                    $productQty={product.quantity}
                  />
                </QuantityBox>
              </MiddleWrapper>
              <RightWrapper>
                <Price>
                  {product.salePrice ? (
                    <>
                      <Paragraph
                        color="var(--color-accent1)"
                        content={formatPrice(product.salePrice)}
                        fontSize="clamp(0.8rem, 2vw, 1rem)"
                        innerStyle={{ fontWeight: "600" }}
                      />
                      <Paragraph
                        content={formatPrice(product.regularPrice)}
                        innerStyle={{ textDecoration: "line-through" }}
                        fontSize="clamp(0.7rem, 2vw, 1rem)"
                      />
                    </>
                  ) : (
                    <Paragraph
                      content={formatPrice(product.price)}
                      fontSize="clamp(0.8rem, 2vw, 1rem)"
                      innerStyle={{ fontWeight: "600" }}
                    />
                  )}
                </Price>
                <RemoveButton
                  onClick={() => {
                    setIsLoading(true);
                    removeProduct(product.id);
                    setTimeout(() => {
                      setIsLoading(false);
                    }, 2000);
                  }}
                >
                  {_("Ta vekk")} <RemoveIcon icon={faTrashCan} />
                </RemoveButton>
              </RightWrapper>
            </ProductWrapper>
          ))}
          <BottomDiv>
            <div className="button-container">
              {wp.wcSettings.checkoutUrl && (
                <>
                  <Button
                    url={isLoading ? "#" : wp.wcSettings.checkoutUrl}
                    onClick={() => {
                      if (isLoading) return;
                      const currentUrl = window.location.pathname;
                      const checkoutUrl = wp.wcSettings.checkoutUrl;

                      if (
                        typeof window !== "undefined" &&
                        currentUrl === checkoutUrl &&
                        typeof onClose === "function"
                      ) {
                        onClose();
                      }
                    }}
                    variant="filled"
                    style={{
                      width: "100%",
                    }}
                  >
                    {isLoading ? (
                      <LoadingIcon icon={faSpinner} className="fa-spin" />
                    ) : (
                      <ButtonTitle>
                        <span>{_("Til kassen")}</span>
                        <span>
                          {new Intl.NumberFormat("nb-NO", {
                            style: "currency",
                            currency: wp.wcSettings.currency,
                          }).format(total)}
                        </span>
                      </ButtonTitle>
                    )}
                  </Button>
                </>
              )}
            </div>
            <DinteroPayments isBig={true} style={{ margin: "0 auto" }} />
            <ShareButton onClick={handleButtonClick}>
              {isLoading ? (
                <LoadingIcon icon={faSpinner} className="fa-spin" />
              ) : (
                <>
                  {_("Del handlekurven din")}
                  <ShareIcon icon={faShare} className="fa-share" />
                </>
              )}
            </ShareButton>
            {isPopupOpen && cartLink && (
              <MiniShare onClose={handleButtonClick} cartLink={cartLink} />
            )}
          </BottomDiv>
        </Wrapper>
      ) : (
        <CartEmptyWrapper>
          <CartEmptyIcon icon={faCartShopping} />
          <EmptyCartMessage>{_("Handlekurven din er tom.")}</EmptyCartMessage>
          {wpProductCategory && (
            <Button
              url={wpProductCategory?.uri}
              label={_("Begynn å handle")}
              variant="outlined"
              padding="0.5rem 0.9rem"
            />
          )}
        </CartEmptyWrapper>
      )}
    </Container>
  );
};

MiniCart.propTypes = {
  onClose: PropTypes.func,
};

export default MiniCart;
